export interface ModelConfig {
  id: string;
  label: string;
  description: string;
  requiresWebGPU: boolean;
  iOSCompatible: boolean;
  inputKey: string;
  outputKey: string;
  needsSigmoid: boolean;
  interactive?: boolean;
  modelOptions?: Record<string, unknown>;
  iosModelOptions?: Record<string, unknown>;
  processorOptions?: Record<string, unknown>;
  iosProcessorOptions?: Record<string, unknown>;
}

export const DEFAULT_MODEL_ID = "briaai/RMBG-1.4";

export const MODEL_CONFIGS: ModelConfig[] = [
  {
    id: "briaai/RMBG-1.4",
    label: "RMBG 1.4",
    description: "Cross-browser",
    requiresWebGPU: false,
    iOSCompatible: true,
    inputKey: "input",
    outputKey: "output",
    needsSigmoid: false,
    iosModelOptions: { config: { model_type: "custom" } },
    processorOptions: {
      revision: "main",
      config: {
        do_normalize: true,
        do_pad: true,
        do_rescale: true,
        do_resize: true,
        image_mean: [0.5, 0.5, 0.5],
        feature_extractor_type: "ImageFeatureExtractor",
        image_std: [0.5, 0.5, 0.5],
        resample: 2,
        rescale_factor: 0.00392156862745098,
        size: { width: 1024, height: 1024 },
      },
    },
    iosProcessorOptions: {
      config: {
        do_normalize: true,
        do_pad: false,
        do_rescale: true,
        do_resize: true,
        image_mean: [0.5, 0.5, 0.5],
        feature_extractor_type: "ImageFeatureExtractor",
        image_std: [1, 1, 1],
        resample: 2,
        rescale_factor: 0.00392156862745098,
        size: { width: 1024, height: 1024 },
      },
    },
  },
  {
    id: "Xenova/modnet",
    label: "MODNet",
    description: "WebGPU accelerated",
    requiresWebGPU: true,
    iOSCompatible: false,
    inputKey: "input",
    outputKey: "output",
    needsSigmoid: false,
    modelOptions: {
      device: "webgpu",
      config: { model_type: "modnet", architectures: ["MODNet"] },
    },
  },
  {
    id: "onnx-community/BiRefNet_lite-ONNX",
    label: "BiRefNet Lite",
    description: "High quality, lightweight",
    requiresWebGPU: false,
    iOSCompatible: false,
    inputKey: "input_image",
    outputKey: "output_image",
    needsSigmoid: true,
    modelOptions: { dtype: "fp32" },
  },
  {
    id: "briaai/RMBG-2.0",
    label: "RMBG 2.0",
    description: "Best quality (experimental)",
    requiresWebGPU: false,
    iOSCompatible: false,
    inputKey: "input_image",
    outputKey: "output_image",
    needsSigmoid: true,
  },
  {
    id: "onnx-community/mediapipe_selfie_segmentation",
    label: "MediaPipe Selfie",
    description: "Fast, people/selfies",
    requiresWebGPU: false,
    iOSCompatible: false,
    inputKey: "pixel_values",
    outputKey: "alphas",
    needsSigmoid: false,
    modelOptions: { dtype: "fp32" },
  },
  {
    id: "Xenova/sam-vit-base",
    label: "SAM (Interactive)",
    description: "Click to segment",
    requiresWebGPU: false,
    iOSCompatible: false,
    interactive: true,
    inputKey: "pixel_values",
    outputKey: "pred_masks",
    needsSigmoid: false,
  },
];

export function getModelConfig(modelId: string): ModelConfig | undefined {
  return MODEL_CONFIGS.find((m) => m.id === modelId);
}

export function getAvailableModels(
  isWebGPUSupported: boolean,
  isIOS: boolean
): ModelConfig[] {
  if (isIOS) {
    return MODEL_CONFIGS.filter((m) => m.iOSCompatible);
  }
  return MODEL_CONFIGS.filter((m) => !m.requiresWebGPU || isWebGPUSupported);
}
